import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, Eye, User, ChevronDown, ExternalLink, Lock, Clock, Github, X } from 'lucide-react';
import { enhanceCodeBlocks } from '@/utils/enhanceCodeBlocks';
import { generateArticleSchema, generateBreadcrumbSchema, truncateDescription, cleanTextForMeta } from '@/utils/seoHelpers';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ProgressButton from '@/components/ProgressButton';
import ShareSection from '@/components/ShareSection';
import CommentSection from '@/components/CommentSection';
import TableOfContents from '@/components/TableOfContents';
import { PostDetailSkeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';

interface Post {
  id: string;
  title: string;
  slug: string;
  description: string;
  thumbnail: string;
  category: string[];
  date: string;
  content?: string;
  is_project?: boolean;
  project_preview_url?: string;
  project_preview_label?: string;
  project_source_url?: string;
  project_source_label?: string;
}

const PostDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewCount, setViewCount] = useState(0);
  const [showMobileTOC, setShowMobileTOC] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Link shortener states
  const [showTimer, setShowTimer] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState('');
  const [linkData, setLinkData] = useState<any>(null);
  const [passwordError, setPasswordError] = useState('');

  const token = searchParams.get('token');
  const progress = ((15 - timeLeft) / 15) * 100;

  useEffect(() => {
    fetchPost();
    
    // Handle link shortener if token present
    if (token) {
      handleShortenedLink(token);
    }
  }, [slug, token]);


  const handleShortenedLink = async (token: string) => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      // Fetch link data
      const { data: link, error } = await supabase
        .from('shortened_links')
        .select('*')
        .or(`alias.eq.${token},token.eq.${token}`)
        .single();

      if (error || !link) {
        console.error('Link not found');
        return;
      }

      setLinkData(link);
      setShowTimer(true);

      // Start timer
      const countdown = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            setShowTimer(false);
            setShowScrollButton(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdown);
    } catch (error) {
      console.error('Error handling shortened link:', error);
    }
  };

  const handleScrollDown = async () => {
    // Scroll to comments section instead of bottom
    const commentsSection = document.querySelector('.comment-section-container');
    if (commentsSection) {
      commentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setShowScrollButton(false);
    
    // Increment click count NOW (after user clicks scroll down button)
    if (linkData) {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        await supabase.functions.invoke('increment-link-click', {
          body: { link_id: linkData.id }
        });
      } catch (error) {
        console.error('Error tracking click:', error);
      }
    }
    
    if (linkData?.password) {
      setShowPasswordPrompt(true);
    }
  };

  const handlePasswordSubmit = () => {
    if (password === linkData?.password) {
      setShowPasswordPrompt(false);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  const insertThumbnailAfterFirstParagraph = (content: string, thumbnailUrl: string) => {
    const paragraphMatch = content.match(/<\/p>/);
    if (paragraphMatch && paragraphMatch.index !== undefined) {
      const insertPosition = paragraphMatch.index + 4; // After </p>
      const thumbnailHtml = `<div class="my-8"><img src="${thumbnailUrl}" alt="Post thumbnail" class="w-full h-auto rounded-xl shadow-lg object-cover max-h-[400px] md:max-h-[500px]" /></div>`;
      return content.slice(0, insertPosition) + thumbnailHtml + content.slice(insertPosition);
    }
    return content;
  };

  const insertProjectResourcesBeforeLastHeading = (content: string, projectSection: string) => {
    // Find all heading tags (h2, h3, h4, h5, h6)
    const headingMatches = [...content.matchAll(/<h[2-6][^>]*>/gi)];
    
    if (headingMatches.length > 0) {
      // Get the last heading
      const lastHeading = headingMatches[headingMatches.length - 1];
      if (lastHeading.index !== undefined) {
        // Insert before the last heading
        return content.slice(0, lastHeading.index) + projectSection + content.slice(lastHeading.index);
      }
    }
    
    // Fallback: try to insert before last paragraph if no headings found
    const paragraphMatches = [...content.matchAll(/<p[^>]*>/gi)];
    if (paragraphMatches.length > 0) {
      const lastPMatch = paragraphMatches[paragraphMatches.length - 1];
      if (lastPMatch.index !== undefined) {
        return content.slice(0, lastPMatch.index) + projectSection + content.slice(lastPMatch.index);
      }
    }
    
    // Final fallback: append at the end
    return content + projectSection;
  };

  const fetchPost = async () => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { data: currentPost, error: postError } = await supabase
        .from('posts')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .single();
      
      if (postError || !currentPost) {
        navigate('/404');
        return;
      }

      const currentPostData = currentPost as any;
      
      const transformedPost = {
        id: currentPostData.id,
        title: currentPostData.title,
        slug: currentPostData.slug,
        description: currentPostData.description,
        thumbnail: currentPostData.thumbnail,
        category: currentPostData.category,
        date: currentPostData.created_at,
        content: currentPostData.content,
        is_project: currentPostData.is_project || false,
        project_preview_url: currentPostData.project_preview_url || '',
        project_preview_label: currentPostData.project_preview_label || 'Live Preview',
        project_source_url: currentPostData.project_source_url || '',
        project_source_label: currentPostData.project_source_label || 'Source Code',
      };

      setPost(transformedPost);

      const { data: analyticsData } = await supabase
        .from('post_analytics')
        .select('views')
        .eq('post_id', currentPost.id)
        .maybeSingle();
      
      setViewCount(analyticsData?.views || 0);

      try {
        await supabase.functions.invoke('increment-post-view', {
          body: { post_id: currentPost.id }
        });
        setViewCount(prev => prev + 1);
      } catch (viewError) {
        if (import.meta.env.DEV) {
          console.log('View count tracking:', viewError);
        }
      }

      // Fetch related posts based on category
      const { data: allPosts, error: allPostsError } = await supabase
        .from('posts')
        .select('id, title, slug, description, thumbnail, category, created_at')
        .eq('published', true)
        .neq('id', currentPost.id)
        .order('created_at', { ascending: false });

      if (allPostsError) throw allPostsError;

      // Filter related posts by category
      const related = allPosts?.filter(p => 
        p.category.some((cat: string) => currentPost.category.includes(cat))
      ).slice(0, 2).map(post => ({
        id: post.id,
        title: post.title,
        slug: post.slug,
        description: post.description,
        thumbnail: post.thumbnail,
        category: post.category,
        date: post.created_at,
      })) || [];

      setRelatedPosts(related);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching post:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="container mx-auto px-4 pt-32">
          <PostDetailSkeleton />
        </div>
        <Footer />
      </>
    );
  }

  if (!post) {
    return null;
  }

  let contentWithThumbnail = insertThumbnailAfterFirstParagraph(post.content || '', post.thumbnail);
  
  // Insert project resources before last paragraph if it's a project
  if (post.is_project && (post.project_preview_url || post.project_source_url)) {
    const projectSection = `
      <div class="my-8">
        <div class="glass-morphism rounded-2xl p-6 sm:p-8">
          <div class="flex flex-col items-center space-y-4">
            <h3 class="text-xl sm:text-2xl font-bold gradient-text text-center">
              Project Resources
            </h3>
            <p class="text-muted-foreground text-center max-w-md text-sm sm:text-base">
              Use buttons given below to checkout the preview of project or to explore the source code of this project. Need help ? Feel free to contact admin.
            </p>
            <div class="grid grid-cols-2 gap-3 w-full max-w-md pt-2">
              ${post.project_preview_url ? `
                <a href="${post.project_preview_url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90 transition-all rounded-2xl px-4 py-2.5 text-white font-medium text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                  ${post.project_preview_label}
                </a>
              ` : ''}
              ${post.project_source_url ? `
                <a href="${post.project_source_url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center justify-center w-full border border-border hover:bg-muted/50 transition-all rounded-2xl px-4 py-2.5 font-medium text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2"><path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"></path><path d="M9 18c-4.51 2-5-2-7-2"></path></svg>
                  ${post.project_source_label}
                </a>
              ` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
    
    contentWithThumbnail = insertProjectResourcesBeforeLastHeading(contentWithThumbnail, projectSection);
  }

  // SEO structured data
  const articleSchema = generateArticleSchema({
    title: post.title,
    description: truncateDescription(post.description),
    image: post.thumbnail,
    datePublished: post.date,
    author: "InvizHer Team",
    category: post.category[0],
    slug: post.slug
  });

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: "https://invizher.com" },
    { name: post.is_project ? "Projects" : "Posts", url: post.is_project ? "https://invizher.com/projects" : "https://invizher.com/posts" },
    { name: post.title, url: `https://invizher.com/posts/${post.slug}` }
  ]);

  const pageUrl = `https://invizher.com/posts/${post.slug}`;
  const metaDescription = truncateDescription(post.description, 160);
  const metaKeywords = post.category.join(', ');

  return (
    <>
      <Helmet>
        <title>{post.title} | InvizHer</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <meta name="author" content="InvizHer Team" />
        <link rel="canonical" href={pageUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={post.thumbnail} />
        <meta property="og:site_name" content="InvizHer" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content="InvizHer Team" />
        <meta property="article:section" content={post.category[0]} />
        {post.category.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={post.thumbnail} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify([articleSchema, breadcrumbSchema])}
        </script>
      </Helmet>

      <div className="bg-background min-h-screen">
        <Header />
        <ProgressButton />

        <main className="container mx-auto px-4 pt-32 pb-20 lg:pb-12 max-w-7xl">
          
          {/* Timer Section */}
          {showTimer && timeLeft > 0 && (
            <div className="flex justify-center my-8">
              <Card className="w-full max-w-3xl border border-border glass overflow-hidden">
                <div className="p-6 sm:p-8 space-y-4">
                  <div className="text-center space-y-2">
                    <h3 className="text-xl font-semibold text-foreground">
                      Preparing {linkData.link_name} Link
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Please wait {timeLeft} seconds, we are generating your link...
                    </p>
                  </div>

                  <div className="relative">
                    <div className="w-full bg-secondary rounded-full h-3 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-1000 ease-linear"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-muted-foreground font-medium">
                      <span>{progress.toFixed(0)}% Complete</span>
                      <span>{timeLeft}s remaining</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Scroll Down Button */}
          {showScrollButton && (
            <div className="flex justify-center my-6">
              <Card className="glass border-0 w-full max-w-sm p-4">
                <CardContent className="p-4 text-center space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-foreground">Link is Ready!</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Kindly click below button to scroll down and to access your {linkData.link_name} link.</p>
                  <Button
                    onClick={handleScrollDown}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90"
                  >
                    <span className="text-sm">Click to Continue</span>
                    <ChevronDown className="w-4 h-4 ml-2 animate-bounce" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
          
          <article className="mb-8">

            {/* Categories and Title */}
            <div className="glass rounded-2xl p-6 mb-6">
              <div className="flex flex-wrap gap-2 mb-4">
                {post.category.slice(0, 2).map((cat, index) => (
                  <span key={index} className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs rounded-full">
                    {cat}
                  </span>
                ))}
                
                {post.category.length > 2 && (
                  <>
                    {showAllCategories ? (
                      <>
                        <div className="flex flex-wrap gap-2 animate-fade-in">
                          {post.category.slice(2).map((cat, index) => (
                            <span key={index + 2} className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs rounded-full">
                              {cat}
                            </span>
                          ))}
                        </div>
                        <button
                          onClick={() => setShowAllCategories(false)}
                          className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs rounded-full flex items-center gap-1 hover:opacity-90 transition-all animate-fade-in"
                        >
                          <X className="w-3 h-3" />
                          <span>Close</span>
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setShowAllCategories(true)}
                        className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs rounded-full flex items-center gap-1 hover:opacity-90 transition-all"
                      >
                        <span>More</span>
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    )}
                  </>
                )}
              </div>
              
              <h1 className="text-3xl mb-6 md:text-4xl lg:text-5xl font-bold text-gray-700 leading-tight">
                {post.title}
              </h1>

              {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                  
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>InvizHer</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>
                      {new Date(post.date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>{viewCount.toLocaleString()} views</span>
                  </div>
                </div>
              
            </div>

            

            {/* Layout: Desktop TOC + Content */}
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Desktop TOC */}
              <aside className="hidden lg:block lg:w-64 flex-shrink-0">
                <div className="sticky top-32">
                  <TableOfContents content={post.content || ''} />
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0 space-y-6">
                <div 
                  ref={contentRef}
                  className="article-content prose prose-sm sm:prose-base md:prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: enhanceCodeBlocks(contentWithThumbnail) }}
                />

                <div className="pt-6 border-t">
                  <ShareSection />
                </div>

                <div className="border-t pt-6 comment-section-container">
                  <CommentSection postId={post.id} />
                </div>

                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                  <div className="pt-6 border-t">
                    <h2 className="text-xl md:text-2xl font-bold mb-4 gradient-text">
                      Related Articles
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {relatedPosts.map((relatedPost) => (
                        <Link 
                          key={relatedPost.id}
                          to={`/posts/${relatedPost.slug}`}
                          className="group glass rounded-xl overflow-hidden hover:shadow-lg transition-all"
                        >
                          <div className="flex gap-3 p-3">
                            <img 
                              src={relatedPost.thumbnail}
                              alt={relatedPost.title}
                              className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-blue-600 font-medium mb-1">
                                {relatedPost.category[0]}
                              </div>
                              <h3 className="text-sm font-semibold text-foreground group-hover:text-blue-600 transition-colors line-clamp-2 mb-1">
                                {relatedPost.title}
                              </h3>
                              <p className="text-xs text-muted-foreground line-clamp-1">
                                {relatedPost.description}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </article>

          {/* Password Prompt */}
                {showPasswordPrompt && linkData && linkData.password && password !== linkData.password && (
                  <div className="glass rounded-2xl p-6 sm:p-8 border-2 border-primary/50">
                    <div className="flex items-center justify-center mb-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                        <Lock className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-2 text-center text-foreground">Link is Protected</h3>
                    <p className="text-sm text-muted-foreground text-center mb-6">To access {linkData.link_name} link, you need password verification. If has, enter it below or else contact admin for password.</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Input
                        type="password"
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                        className="flex-1 text-sm sm:text-base"
                      />
                      <Button 
                        onClick={handlePasswordSubmit} 
                        className="bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90 w-full sm:w-auto"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Submit
                      </Button>
                    </div>
                    {passwordError && (
                      <p className="text-destructive text-sm mt-3 text-center bg-destructive/10 p-2 rounded-lg">
                        {passwordError}
                      </p>
                    )}
                  </div>
                )}

                {/* Link Access After Password Verification or No Password */}
                {linkData && (!linkData.password || (linkData.password && password === linkData.password)) && (
                  <div className="glass rounded-2xl p-6 sm:p-8 text-center">
                    <Button
                      onClick={() => window.open(linkData.original_url, '_blank')}
                      size="lg"
                      className="bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90 hover:scale-105 transition-all px-6 sm:px-8 py-5 sm:py-6"
                    >
                     Access {linkData.link_name} Link
                    </Button>
                    <p className="text-sm text-muted-foreground mt-4">Here's your final link, click above button to visit {linkData.link_name} link.</p>
                  </div>
                )}
          
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PostDetail;
